import React, { useState, useContext } from "react";
import { withRouter, useHistory } from "react-router-dom";
import { Button } from "@material-ui/core";
import Page from "components/common_components/Page/Page";
import { useDispatch, useSelector } from "react-redux";
import { notifyWarning } from "redux/actions/Notifications";
import POSContext from "./POSContext";
import { Form as FormikForm, DatePickerField, AutocompleteField } from "components/common_components/Formik";
import * as Yup from "yup";
import Grid from "@material-ui/core/Grid";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import Report from "./Report";
import { hasElements } from "utils/arrays";
import Header from "components/common_components/Header";

const reports = [
    {
        id: "sales",
        name: "Sales",
        requiresDates: true,
        onlyForAdmins: true,
    },
    { id: "sales-per-users", name: "Detailed Sales", requiresDates: true, onlyForAdmins: true },
];

function Reports() {
    const dispatch = useDispatch();
    const history = useHistory();
    const { user } = useSelector((state) => state.Login);

    const { Reports, documentIssuer } = useContext(POSContext);

    const [downloadLinkReady, setDownloadLinkReady] = useState(false);
    const [report, setReport] = useState(undefined);
    const [reportProps, setReportProps] = useState(undefined);

    const validationSchema = Yup.lazy((values) => {
        return Yup.object({
            report: Yup.string().required("Required field"),
            initialDate: Yup.date().required("Required field"),
            finalDate: Yup.date().required("Required field"),
        });
    });

    function getReportOptions() {
        if (user.isAdmin) {
            return reports;
        } else {
            return reports.filter((x) => !x.onlyForAdmins);
        }
    }

    function onCancelHandler({ setSubmitting, resetForm }) {
        setReport(undefined);
        setReportProps(undefined);
        setDownloadLinkReady(false);
        setSubmitting(false);
        resetForm({ report: getReportOptions()[0], initialDate: new Date(), finalDate: new Date() });
    }

    async function onSubmitHandler(values, { setSubmitting, setErrors, resetForm }) {
        const initialDateInMillis = values.initialDate.setHours(0, 0, 0, 0);
        const finalDateInMillis = values.finalDate.setHours(23, 59, 59, 999);

        if (Number(initialDateInMillis) > Number(finalDateInMillis)) {
            setErrors({ initialDate: "You can't set the Initial Date after the Final Date." });
            return;
        }

        if (values.report.id === "sales") {
            const sales = await Reports.getSalesByDateRangeAsync(initialDateInMillis, finalDateInMillis);

            if (hasElements(sales)) {
                setReportProps({
                    salesData: sales,
                    initialDateInMillis,
                    finalDateInMillis,
                    documentIssuer,
                });
                setReport(values.report.id);
                setDownloadLinkReady(true);
                setSubmitting(false);
            } else {
                dispatch(notifyWarning("No sales found!"));
                onCancelHandler({ setSubmitting, resetForm });
            }
        }

        if (values.report.id === "sales-per-users") {
            const sales = await Reports.getSalesByDateRangePerUsersAsync(initialDateInMillis, finalDateInMillis);

            if (hasElements(sales)) {
                setReportProps({
                    salesData: sales,
                    initialDateInMillis,
                    finalDateInMillis,
                    documentIssuer,
                });
                setReport(values.report.id);
                setDownloadLinkReady(true);
                setSubmitting(false);
            } else {
                dispatch(notifyWarning("No sales found!"));
                onCancelHandler({ setSubmitting, resetForm });
            }
        }
    }

    const viewerEnabled = Boolean(downloadLinkReady && report && reportProps);

    return (
        <Page>
            <Header title="POS Reports" />
            <div className="layout">
                <Grid container direction="row" spacing={3}>
                    <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
                        <FormikForm
                            onSubmit={onSubmitHandler}
                            validateOnChange={false}
                            initialValues={{
                                report: getReportOptions()[0],
                                initialDate: new Date(),
                                finalDate: new Date(),
                            }}
                            validationSchema={validationSchema}
                            showButtons={false}
                        >
                            {({ values, ...form }) => (
                                <Grid container spacing={3} direction="column">
                                    <Grid item>
                                        <AutocompleteField
                                            disabled={viewerEnabled}
                                            fieldName="report"
                                            label="Report *"
                                            options={getReportOptions()}
                                            valueKey="id"
                                            labelKey="name"
                                            getOptionLabel={(option) => option.name}
                                        />
                                    </Grid>
                                    {values.report && values.report.requiresDates && (
                                        <>
                                            <Grid item>
                                                <DatePickerField disabled={viewerEnabled} fieldName="initialDate" label="Initial Date *" />
                                            </Grid>
                                            <Grid item>
                                                <DatePickerField disabled={viewerEnabled} fieldName="finalDate" label="Final Date *" />
                                            </Grid>
                                        </>
                                    )}
                                    <Grid item>
                                        <Grid container spacing={1} direction="row-reverse">
                                            {!viewerEnabled && (
                                                <Grid item>
                                                    <Button variant="text" type="submit" color="primary" endIcon={<PlayArrowIcon />}>
                                                        Execute
                                                    </Button>
                                                </Grid>
                                            )}
                                            <Grid item>
                                                <Button onClick={() => onCancelHandler(form)} variant="text" type="reset" color="default">
                                                    Cancel
                                                </Button>
                                            </Grid>
                                            <Grid item>
                                                <Button
                                                    onClick={() => {
                                                        onCancelHandler(form);
                                                        history.goBack();
                                                    }}
                                                    variant="text"
                                                    type="reset"
                                                    color="default"
                                                >
                                                    Back
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                        </FormikForm>
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={9} xl={9}>
                        {viewerEnabled && <Report reportProps={reportProps} reportType={report} />}
                    </Grid>
                </Grid>
            </div>
        </Page>
    );
}

export default withRouter(Reports);
